import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import authService from '../services/authService';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Email is required');
      setStatus('error');
      return;
    }
    try {
      setStatus('loading');
      setError('');
      const response = await authService.forgotPassword(email.trim());
      setMessage(response.message);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.error || 'Failed to send reset email');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === 'success' ? (
            <CheckCircle className="h-12 w-12 text-success mx-auto mb-2" />
          ) : (
            <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
          )}
          <CardTitle className="text-2xl">
            {status === 'success' ? 'Check Your Email' : 'Forgot Password'}
          </CardTitle>
          <CardDescription>
            {status === 'success'
              ? 'We\'ve sent a password reset link to your email address.'
              : 'Enter your email address and we\'ll send you a link to reset your password.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status !== 'success' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status === 'loading'}
                />
              </div>

              {status === 'error' && error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={status === 'loading'}>
                {status === 'loading' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {status === 'loading' ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          )}

          {status === 'success' && message && (
            <Alert className="bg-success/10 text-success border-success/20">
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Login
          </Button>

          {status === 'success' && (
            <div className="p-4 bg-muted rounded-lg text-sm text-muted-foreground">
              <p className="font-medium mb-2">Didn't receive the email?</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Check your spam/junk folder</li>
                <li>The link expires in 2 hours</li>
                <li>Try again with a different email if needed</li>
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
